import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Tag, Empty, Spin, message, Modal, Form } from 'antd';
import { SearchOutlined, PlusOutlined, ReloadOutlined, CameraOutlined, CopyOutlined } from '@ant-design/icons';
import useCompanyNavigate from '../../utils/useCompanyNavigate';
import { departmentBatchApi } from '../../services/api';

const { Search } = Input;

const photoTypeNames = {
  1: '原石', 3: '成品', 4: '证书', 5: '切料', 6: '规划', 7: '手修', 8: '抛光', 9: '原石入库', 10: '切料标块'
};

const Batches = () => {
  const navigate = useCompanyNavigate();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [keyword, setKeyword] = useState('');
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyingBatch, setCopyingBatch] = useState(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyForm] = Form.useForm();

  const deptAdmin = JSON.parse(localStorage.getItem('department_admin') || '{}');
  const canCreateBatch = deptAdmin.can_create_batch === 1;
  const canCopyBatch = deptAdmin.can_copy_batch === 1;
  const photoTypes = deptAdmin.photo_types ? deptAdmin.photo_types.split(',').map(Number) : [];

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const res = await departmentBatchApi.list({ page, limit: 20, keyword });
      if (res.success) {
        setList(res.data.list);
        setPagination({ ...pagination, current: page, total: res.data.pagination.total });
      }
    } catch (err) {
      message.error('获取批次列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusTag = (status) => {
    const map = { 0: { text: '未开始', color: 'default' }, 1: { text: '生产中', color: 'processing' }, 2: { text: '已完成', color: 'success' } };
    const s = map[status] || { text: '未知', color: 'default' };
    return <Tag color={s.color}>{s.text}</Tag>;
  };

  const handleCopyClick = (e, batch) => {
    e.stopPropagation();
    setCopyingBatch(batch);
    copyForm.setFieldsValue({ batchNo: batch.batch_no, name: batch.name });
    setCopyModalOpen(true);
  };

  const handleCopySubmit = async () => {
    try {
      const values = await copyForm.validateFields();
      setCopyLoading(true);
      const res = await departmentBatchApi.copy(copyingBatch.id, values);
      message.success(res.message || '复制成功');
      setCopyModalOpen(false);
      fetchData(1);
    } catch (err) {
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      }
    } finally {
      setCopyLoading(false);
    }
  };

  return (
    <div>
      {/* 搜索栏 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Search
          placeholder="搜索批次编号或名称"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          onSearch={() => fetchData(1)}
          allowClear
          style={{ flex: 1 }}
        />
        <Button icon={<ReloadOutlined />} onClick={() => fetchData(pagination.current)} />
        {canCreateBatch && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/department/batches/new')}
            style={{ background: '#8B7355' }}
          >
            新建
          </Button>
        )}
      </div>

      {/* 部门标识 */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: '#999' }}>可上传：</span>
        {photoTypes.map(t => (
          <Tag key={t} color="volcano" style={{ fontSize: 12 }}>{photoTypeNames[t] || `类型${t}`}</Tag>
        ))}
      </div>

      {/* 批次列表 */}
      <Spin spinning={loading}>
        {list.length === 0 ? (
          <Empty description="暂无批次" style={{ marginTop: 60 }} />
        ) : (
          list.map(batch => (
            <Card
              key={batch.id}
              size="small"
              style={{ marginBottom: 10, borderRadius: 8, cursor: 'pointer' }}
              onClick={() => navigate(`/department/batches/${batch.id}`)}
              hoverable
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{batch.batch_no}</span>
                    {getStatusTag(batch.status)}
                  </div>
                  <div style={{ fontSize: 13, color: '#666' }}>{batch.name}</div>
                  {batch.jade_type_name && (
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>料种：{batch.jade_type_name}</div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {canCopyBatch && (
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={(e) => handleCopyClick(e, batch)}
                      style={{ color: '#8B7355', borderColor: '#8B7355' }}
                    >
                      复制
                    </Button>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#8B7355' }}>
                    <CameraOutlined />
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{batch.my_photo_count || 0}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </Spin>

      {/* 加载更多 */}
      {list.length < pagination.total && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button size="small" onClick={() => fetchData(pagination.current + 1)}>
            加载更多
          </Button>
        </div>
      )}

      {/* 复制批次弹框 */}
      <Modal
        title="复制批次"
        open={copyModalOpen}
        onOk={handleCopySubmit}
        onCancel={() => setCopyModalOpen(false)}
        confirmLoading={copyLoading}
        okText="确定复制"
        cancelText="取消"
      >
        <Form form={copyForm} layout="vertical">
          <Form.Item name="batchNo" label="批次编号" rules={[{ required: true, message: '请输入批次编号' }]}>
            <Input placeholder="请输入新的批次编号" />
          </Form.Item>
          <Form.Item name="name" label="批次名称" rules={[{ required: true, message: '请输入批次名称' }]}>
            <Input placeholder="请输入新的批次名称" />
          </Form.Item>
        </Form>
        <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
          将复制原石照片和切料照片到新批次
        </div>
      </Modal>
    </div>
  );
};

export default Batches;
